<template>
  <form ref="form" class="form form--initialized form--desktop-horizontal" v-if="!isFormSend && !formSendError" @submit.prevent="handleSubmit">
    <div class="form__body">
      <div class="form__group">
        <div role="group" class="form__control form__control--desktop-horizontal" :aria-labelledby="'step1-' + uniqueId">
          <div class="form__control-header">
            <div class="form__control-text">
              <div class="form__control-headlines" :id="'step1-' + uniqueId">
                <span class="form__control-headline headline headline--h4 ">{{ t('franchise.steps.1.title') }}</span>
              </div>
            </div>
          </div>
          <div class="form__control-content">
            <div class="form__control-content-inner">
              <div class="form__row">
                <div class="row">
                  <div class="col-12 col-xl-6">
                    <InputField :label="t('franchise.firstname.label')"
                                id="franchise-firstname"
                                v-model="formData.firstname"
                                :inverted="true"
                                @validation-change="handleFirstNameValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="firstnameRules"
                                :placeholder="t('franchise.firstname.placeholder')"
                                autocomplete="given-name" />
                  </div>
                  <div class="col-12 col-xl-6">
                    <InputField :label="t('franchise.lastname.label')"
                                id="franchise-lastname"
                                v-model="formData.lastname"
                                :inverted="true"
                                @validation-change="handleLastNameValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="lastnameRules"
                                :placeholder="t('franchise.lastname.placeholder')"
                                autocomplete="family-name" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-xl-9">
                    <InputField :label="t('franchise.street.label')"
                                id="franchise-street"
                                v-model="formData.street"
                                :inverted="true"
                                @validation-change="handleStreetValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="streetRules"
                                :placeholder="t('franchise.street.placeholder')"
                                autocomplete="address-line1" />
                  </div>
                  <div class="col-12 col-xl-3">
                    <InputField :label="t('franchise.no.label')"
                                id="et-no"
                                v-model="formData.no"
                                :inverted="true"
                                @validation-change="handleNoValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="noRules"
                                :placeholder="t('franchise.no.placeholder')"
                                autocomplete="address-line2" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-xl-4">
                    <InputField :label="t('franchise.zip.label')"
                                id="franchise-zip"
                                v-model="formData.zip"
                                :inverted="true"
                                @validation-change="handleZipValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="zipRules"
                                :placeholder="t('franchise.zip.placeholder')"
                                autocomplete="postal-code" />
                  </div>
                  <div class="col-12 col-xl-8">
                    <InputField :label="t('franchise.city.label')"
                                id="franchise-city"
                                v-model="formData.city"
                                :inverted="true"
                                @validation-change="handleCityValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="cityRules"
                                :placeholder="t('franchise.city.placeholder')"
                                autocomplete="address-level2" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-xl-6">
                    <InputField :label="t('franchise.nationality.label')"
                                id="franchise-nationality"
                                v-model="formData.nationality"
                                :inverted="true"
                                @validation-change="handleNationalityValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="nationalityRules"
                                :placeholder="t('franchise.nationality.placeholder')"
                                autocomplete="off" />
                  </div>
                  <div class="col-12 col-xl-6">
                    <SelectField :label="t('franchise.martial_state.label')"
                                  id="franchise-martial_state"
                                  v-model="formData.martial_state"
                                  :options="tm('franchise.martial_state.options')"
                                  @validation-change="handleMartialStateValidation"
                                  :force-validation="forceValidationOnSubmit"
                                  :rules="martialStateRules"
                                  :inverted="true"
                                  :large="true" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-xl-6">
                    <InputField :label="t('franchise.birthday.label')"
                                id="franchise-birthday"
                                v-model="formData.birthday"
                                :inverted="true"
                                @validation-change="handleBirthdayValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="birthdayRules"
                                type="date"
                                :placeholder="t('franchise.birthday.placeholder')"
                                autocomplete="off" />
                  </div>
                  <div class="col-12 col-xl-6">
                    <InputField :label="t('franchise.place_of_birth.label')"
                                id="franchise-place_of_birth"
                                v-model="formData.place_of_birth"
                                :inverted="true"
                                @validation-change="handlePlaceOfBirthValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="placeOfBirthRules"
                                :placeholder="t('franchise.place_of_birth.placeholder')"
                                autocomplete="off" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-xl-6">
                    <InputField :label="t('franchise.profession.label')"
                                id="franchise-profession"
                                v-model="formData.profession"
                                :inverted="true"
                                @validation-change="handleProfessionValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="professionRules"
                                :placeholder="t('franchise.profession.placeholder')"
                                autocomplete="organization-title" />
                  </div>
                  <div class="col-12 col-xl-6">
                    <InputField :label="t('franchise.employer.label')"
                                id="franchise-employer"
                                v-model="formData.employer"
                                :inverted="true"
                                @validation-change="handleEmployerValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="employerRules"
                                :placeholder="t('franchise.employer.placeholder')"
                                autocomplete="organization" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <InputField :label="t('franchise.email.label')"
                                id="franchise-email"
                                v-model="formData.email"
                                :inverted="true"
                                @validation-change="handleEmailValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="emailRules"
                                :placeholder="t('franchise.email.placeholder')"
                                autocomplete="email" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <InputField :label="t('franchise.phone.label')"
                                id="franchise-phone"
                                v-model="formData.phone"
                                :inverted="true"
                                @validation-change="handlePhoneValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="phoneRules"
                                :placeholder="t('franchise.email.placeholder')"
                                autocomplete="tel" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form__group">
        <div role="group" class="form__control form__control--desktop-horizontal" :aria-labelledby="'step1-' + uniqueId">
          <div class="form__control-header">
            <div class="form__control-text">
              <div class="form__control-headlines" :id="'step1-' + uniqueId">
                <span class="form__control-headline headline headline--h4 ">{{ t('franchise.steps.2.title') }}</span>
              </div>
              <div class="form__control-description" id="nS67Cs">{{ t('franchise.steps.2.copy') }}</div>
            </div>
          </div>
          <div class="form__control-content">
            <div class="form__control-content-inner">
              <div class="form__row">
                <div class="row">
                  <div class="col-12">
                    <InputField :label="t('franchise.location.label')"
                                id="franchise-location"
                                v-model="formData.location"
                                :inverted="true"
                                @validation-change="handleLocationValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="locationRules"
                                :placeholder="t('franchise.location.placeholder')"
                                autocomplete="off" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form__group">
        <div role="group" class="form__control form__control--desktop-horizontal" :aria-labelledby="'step1-' + uniqueId">
          <div class="form__control-header">
            <div class="form__control-text">
              <div class="form__control-headlines" :id="'step1-' + uniqueId">
                <span class="form__control-headline headline headline--h4 ">{{ t('franchise.steps.3.title') }}</span>
              </div>
              <div class="form__control-description" id="nS67Cs">{{ t('franchise.steps.3.copy') }}</div>
            </div>
          </div>
          <div class="form__control-content">
            <div class="form__control-content-inner">
              <div class="form__row">
                <div class="row">
                  <div class="col-12">
                    <InputField :label="t('franchise.capital.label')"
                                id="franchise-capital"
                                v-model="formData.capital"
                                :inverted="true"
                                @validation-change="handleCapitalValidation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="capitalRules"
                                :placeholder="t('franchise.capital.placeholder')"
                                autocomplete="off" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form__group form__group--tablet-rounded">
        <div role="group" class="form__control form__control--desktop-horizontal" :aria-labelledby="'step1-' + uniqueId">
          <div class="form__control-header">
            <div class="form__control-text">
              <div class="form__control-headlines" :id="'step1-' + uniqueId">
                <span class="form__control-headline headline headline--h4 ">{{ t('franchise.steps.4.title') }}</span>
              </div>
              <div class="form__control-description" id="nS67Cs">{{ t('franchise.steps.4.copy') }}</div>
            </div>
          </div>
          <div class="form__control-content form__control-content--desktop-pb-xxl">
            <div class="form__control-content-inner">
              <div class="form__row">
                <div class="row">
                  <div class="col-12">
                    <TextareaField :label="t('franchise.goal_1.label')"
                                id="franchise-goal_1"
                                v-model="formData.goal_1"
                                :inverted="true"
                                @validation-change="handleGoal1Validation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="goal1Rules"
                                :placeholder="t('franchise.goal_1.placeholder')"
                                autocomplete="off" />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <TextareaField :label="t('franchise.goal_2.label')"
                                id="franchise-goal_1"
                                v-model="formData.goal_2"
                                :inverted="true"
                                @validation-change="handleGoal2Validation"
                                :force-validation="forceValidationOnSubmit"
                                :rules="goal2Rules"
                                :placeholder="t('franchise.goal_2.placeholder')"
                                autocomplete="off" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="form__footer-panel">
      <div class="form__row form__row--mb-lg">
        <div class="row">
          <div class="col-12">
            <Checkbox id="franchise-data"
                      v-model="formData.data"
                      :inverted="true"
                      @validation-change="handleDataValidation"
                      :force-validation="forceValidationOnSubmit"
                      :rules="dataRules"
                      :helptext="`${t('franchise.data.helptextPreLink')}<a href='${countryLanguagePath}${t('franchise.data.link')}' class='link link--negative' target='_blank'>${t('franchise.data.helptextLink')}</a>${t('franchise.data.helptextPostLink')}`"
                      :label="t('franchise.data.label')"/>
            <Checkbox id="franchise-privacy"
                      v-model="formData.privacy"
                      :inverted="true"
                      @validation-change="handlePrivacyValidation"
                      :force-validation="forceValidationOnSubmit"
                      :rules="privacyRules"
                      :helptext="t('franchise.privacy.helptext')"
                      :label="t('franchise.privacy.label')"/>
            <Checkbox id="et-newsletter"
                      v-model="formData.newsletter"
                      :inverted="true"
                      :helptext="`${t('franchise.newsletter.helptextPreLink')}<a href='${countryLanguagePath}${t('franchise.newsletter.link')}' class='link link--negative' target='_blank'>${t('franchise.newsletter.helptextLink')}</a>${t('franchise.newsletter.helptextPostLink')}`"
                      :label="t('franchise.newsletter.label')"/>
          </div>
        </div>
      </div>
      <div class="form__row form__row--text-center">
        <Submit :loading="isSubmitLoading"
                :label="t('et.submit.label')" />
      </div>
    </div>
  </form>
  <div class="simple-2col simple-2col--with-navy-bg simple-2col--gap-lg" aria-live="polite" v-if="isFormSend && !formSendError">
    <div class="simple-2col__col simple-2col__col--with-navy-bg simple-2col__col--align-center simple-2col__col--no-padding-top simple-2col__col--no-padding-x">
      <div class="simple-2col__header">
        <h2 class="headline headline--h1">{{ t('franchise.success.title') }}</h2>
      </div>
      <div class="simple-2col__content">
        <p>{{ t('franchise.success.copy') }}</p>
      </div>
    </div>
    <div class="simple-2col__col simple-2col__col--with-blue-bg simple-2col__col--align-center">
      <span class="icon icon--lg">
        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34">
          <path d="M17 30C9.832 30 4 24.168 4 17S9.832 4 17 4s13 5.832 13 13-5.832 13-13 13Zm0-24.953C10.408 5.047 5.047 10.408 5.047 17S10.408 28.953 17 28.953 28.953 23.592 28.953 17 23.592 5.047 17 5.047Z"></path>
          <path fill-rule="evenodd" d="M11.428 14.2a1.019 1.019 0 0 0-.028.24v5.959c0 .571.461.993.978.993h9.226a.986.986 0 0 0 .979-.993v-5.991l-5.295 3.146a.585.585 0 0 1-.609-.01l-5.251-3.343Zm.832-.746 4.742 3.02 4.97-2.954a.962.962 0 0 0-.368-.073h-9.226c-.04 0-.08.002-.118.007Zm-1.978.986c0-1.111.915-2.053 2.096-2.053h9.226c1.181 0 2.097.941 2.097 2.053v5.959c0 1.111-.916 2.053-2.097 2.053h-9.226c-1.18 0-2.096-.942-2.096-2.053v-5.96Z" clip-rule="evenodd"></path>
        </svg>
      </span>
      <h3 class="headline headline--h4">{{ t('franchise.success.email') }}</h3>
      <p>{{ t('franchise.success.emailCopy') }}.</p>
    </div>
  </div>
  <div class="simple-2col simple-2col--with-navy-bg simple-2col--gap-lg" aria-live="polite" v-if="!isFormSend && formSendError">
    <div class="simple-2col__col simple-2col__col--with-navy-bg simple-2col__col--align-center simple-2col__col--no-padding-top simple-2col__col--no-padding-x">
      <div class="simple-2col__header">
        <h2 class="headline headline--h1">{{ t('franchise.error.title') }}</h2>
      </div>
      <div class="simple-2col__content">
        <p>{{ t('franchise.error.copy') }}</p>
      </div>
    </div>
    <div class="simple-2col__col simple-2col__col--with-blue-bg simple-2col__col--align-center">
      <span class="icon icon--lg">
        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" fill="currentColor" viewBox="0 0 34 34">
          <path d="M17 30C9.832 30 4 24.168 4 17S9.832 4 17 4s13 5.832 13 13-5.832 13-13 13Zm0-24.953C10.408 5.047 5.047 10.408 5.047 17S10.408 28.953 17 28.953 28.953 23.592 28.953 17 23.592 5.047 17 5.047Z"></path>
          <path fill-rule="evenodd" d="M11.428 14.2a1.019 1.019 0 0 0-.028.24v5.959c0 .571.461.993.978.993h9.226a.986.986 0 0 0 .979-.993v-5.991l-5.295 3.146a.585.585 0 0 1-.609-.01l-5.251-3.343Zm.832-.746 4.742 3.02 4.97-2.954a.962.962 0 0 0-.368-.073h-9.226c-.04 0-.08.002-.118.007Zm-1.978.986c0-1.111.915-2.053 2.096-2.053h9.226c1.181 0 2.097.941 2.097 2.053v5.959c0 1.111-.916 2.053-2.097 2.053h-9.226c-1.18 0-2.096-.942-2.096-2.053v-5.96Z" clip-rule="evenodd"></path>
        </svg>
      </span>
      <p class="headline headline--h4">{{ t('franchise.error.email') }}</p>
      <p>{{ t('franchise.error.emailCopy') }} <a class="link link--negative" href="mailto:fnm@kieser.com">fnm@kieser.com</a>.</p>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from 'vue-i18n';
import {computed, onMounted, reactive, ref, useId, useTemplateRef} from 'vue';
import InputField from './components/fields/InputField.vue';
import Submit from './components/fields/Submit.vue';
import {
  getCountryAndLanguagePathFromUrl,
} from '../../abstracts/utilities.js';
import axios from 'axios';
import TextareaField from './components/fields/TextareaField.vue';
import Checkbox from './components/fields/Checkbox.vue';
import SelectField from './components/fields/SelectField.vue';
import {trackData} from './utilities.js';
import Form from '/vendor/skygate/kieser-patternlab/source/javascript/components/form';

const { t, tm } = useI18n();
const uniqueId = useId();
const countryLanguagePath = window.location.origin + '/' + getCountryAndLanguagePathFromUrl();

const formData = reactive({
  firstname: '',
  lastname: '',
  street: '',
  no: '',
  zip: '',
  city: '',
  nationality: '',
  martial_state: '',
  birthday: '',
  place_of_birth: '',
  profession: '',
  employer: '',
  email: '',
  phone: '',
  location: '',
  capital: '',
  goal_1: '',
  goal_2: '',
  data: false,
  privacy: false,
  newsletter: false
});

const form = useTemplateRef('form');
onMounted(() => {
  new Form(form.value);
});

const isValidFirstName = ref(false);
const isValidLastName = ref(false);
const isValidStreet = ref(false);
const isValidNo = ref(false);
const isValidZip = ref(false);
const isValidCity = ref(false);
const isValidNationality = ref(false);
const isValidMartialState = ref(false);
const isValidBirthday = ref(false);
const isValidPlaceOfBirth = ref(false);
const isValidProfession = ref(false);
const isValidEmployer = ref(false);
const isValidEmail = ref(false);
const isValidPhone = ref(false);
const isValidLocation = ref(false);
const isValidCapital = ref(false);
const isValidGoal1 = ref(false);
const isValidGoal2 = ref(false);
const isValidData = ref(false);
const isValidPrivacy = ref(false);

const isFormSend = ref(false);
const formSendError = ref(false);
const isSubmitLoading = ref(false);

const forceValidationOnSubmit = ref(false);

const firstnameRules = [(value) => value.length > 0 || t('franchise.firstname.error.empty') ];
const lastnameRules = [(value) => value.length > 0 || t('franchise.lastname.error.empty') ];
const streetRules = [(value) => value.length > 0 || t('franchise.street.error.empty') ];
const noRules = [(value) => value.length > 0 || t('franchise.no.error.empty') ];
const zipRules = [
  (value) => value.length > 0 || t('franchise.zip.error.empty'),
  (value) => /^[0-9\s]*$/.test(value) || t('franchise.zip.error.digits')
];
const cityRules = [(value) => value.length > 0 || t('franchise.city.error.empty') ];
const nationalityRules = [(value) => value.length > 0 || t('franchise.nationality.error.empty') ];
const martialStateRules = [(value) => value.length > 0 || t('franchise.martial_state.error.empty') ];
const birthdayRules = [(value) => value.length > 0 || t('franchise.birthday.error.empty') ];
const placeOfBirthRules = [(value) => value.length > 0 || t('franchise.place_of_birth.error.empty') ];
const professionRules = [(value) => value.length > 0 || t('franchise.profession.error.empty') ];
const employerRules = [(value) => value.length > 0 || t('franchise.employer.error.empty') ];
const emailRules = [
  (value) => value.length > 0 || t('franchise.email.error.empty'),
  (value) => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value) || t('franchise.email.error.email'),
];
const phoneRules = [
  (value) => value.length > 0  || t('franchise.phone.error.empty'),
  (value) => /^[0-9\s]*$/.test(value) || t('franchise.phone.error.digits'),
];
const locationRules = [(value) => value.length > 0 || t('franchise.location.error.empty') ];
const capitalRules = [(value) => value.length > 0 || t('franchise.capital.error.empty') ];
const goal1Rules = [(value) => value.length > 0 || t('franchise.goal_1.error.empty') ];
const goal2Rules = [(value) => value.length > 0 || t('franchise.goal_2.error.empty') ];
const dataRules = [(value) => value === true || t('franchise.data.error.empty') ];
const privacyRules = [(value) => value === true || t('franchise.privacy.error.empty') ];

const handleFirstNameValidation = (isValid) => {isValidFirstName.value = isValid;};
const handleLastNameValidation = (isValid) => {isValidLastName.value = isValid;};
const handleStreetValidation = (isValid) => {isValidStreet.value = isValid;};
const handleNoValidation = (isValid) => {isValidNo.value = isValid;};
const handleZipValidation = (isValid) => {isValidZip.value = isValid;};
const handleCityValidation = (isValid) => {isValidCity.value = isValid;};
const handleNationalityValidation = (isValid) => {isValidNationality.value = isValid;};
const handleMartialStateValidation = (isValid) => {isValidMartialState.value = isValid;};
const handleBirthdayValidation = (isValid) => {isValidBirthday.value = isValid;};
const handlePlaceOfBirthValidation = (isValid) => {isValidPlaceOfBirth.value = isValid;};
const handleProfessionValidation = (isValid) => {isValidProfession.value = isValid;};
const handleEmployerValidation = (isValid) => {isValidEmployer.value = isValid;};
const handleEmailValidation = (isValid) => {isValidEmail.value = isValid;};
const handlePhoneValidation = (isValid) => {isValidPhone.value = isValid;};
const handleLocationValidation = (isValid) => {isValidLocation.value = isValid;};
const handleCapitalValidation = (isValid) => {isValidCapital.value = isValid;};
const handleGoal1Validation = (isValid) => {isValidGoal1.value = isValid;};
const handleGoal2Validation = (isValid) => {isValidGoal2.value = isValid;};
const handleDataValidation = (isValid) => {isValidData.value = isValid;};
const handlePrivacyValidation = (isValid) => {isValidPrivacy.value = isValid;};

const isFormValid = computed(() => isValidFirstName.value && isValidLastName.value && isValidStreet.value && isValidNo.value && isValidZip.value && isValidCity.value && isValidNationality.value && isValidMartialState.value && isValidBirthday.value && isValidPlaceOfBirth.value && isValidProfession.value && isValidEmployer.value && isValidEmail.value && isValidPhone.value && isValidLocation.value && isValidCapital.value && isValidGoal1.value && isValidGoal2.value && isValidData.value && isValidPrivacy.value);

const handleSubmit = () => {
  forceValidationOnSubmit.value = true;
  isSubmitLoading.value = true;

  setTimeout(() => {
    if (isFormValid.value) {

      axios.post(window.location.origin + '/' + getCountryAndLanguagePathFromUrl() + '/send/franchise/', formData).then(response => {
        console.info(response.status);

        isFormSend.value = true;
        formSendError.value = false;

        trackData({
          'event': 'generate_lead',
          'form_name': 'Franchise'
        });
      }).catch(error => {
        console.error(error.status);

        // server error
        isFormSend.value = false;
        formSendError.value = true;
      });

    } else {
      // form error
      isSubmitLoading.value = false;
    }
  }, 0);
};
</script>